// File: webelieve-frontend/src/features/notes/components/NoteEditor.tsx
// Right panel: rich note editor with collaboration and progressive metadata controls

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { LexicalEventEditor } from '../../calendar/components/LexicalEventEditor';
import { useNotesStore } from '../stores/useNotesStore';
import { useNoteCollaborationStore } from '../stores/useNoteCollaborationStore';
import { noteCollaborationSocket } from '../services/noteCollaborationSocket';
import NoteCollaborationToolbar from './NoteCollaborationToolbar';
import NoteCommentPanel from './NoteCommentPanel';
import NotePrivilegeWarning from './NotePrivilegeWarning';
import NoteIQBadge from './NoteIQBadge';
import NoteVersionHistory from './NoteVersionHistory';
import NoteInviteCollaboratorModal from './NoteInviteCollaboratorModal';
import NoteCollaborationSettingsDrawer from './NoteCollaborationSettingsDrawer';
import NoteContextMenu from './NoteContextMenu';
import { useNoteContextMenu } from '../hooks/useNoteContextMenu';
import { useNoteContextActions } from '../hooks/useNoteContextActions';
import type {
  Note,
  NoteType,
  NotePriority,
  NoteConfidentiality,
  NoteLegalSignificance,
  NoteCasePhase,
  UpdateNoteDto,
} from '../types/note.types';

interface NoteEditorProps {
  note: Note | null;
  clientId?: string;
  matterId?: string;
  focusMode?: boolean;
  onSetFocusMode?: (enabled: boolean) => void;
}

const AUTO_SAVE_DELAY = 1500; // ms
const CURSOR_STALE_MS = 30_000;
const PRESENCE_STALE_MS = 10 * 60_000;
const COMMENT_PING_MS = 8_000;

const NOTE_TYPE_OPTIONS: { value: NoteType; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'case-strategy', label: 'Case Strategy' },
  { value: 'client-communication', label: 'Client Communication' },
  { value: 'court-filing', label: 'Court Filing' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'settlement', label: 'Settlement' },
  { value: 'deposition-summary', label: 'Deposition Summary' },
  { value: 'meeting-notes', label: 'Meeting Notes' },
  { value: 'research', label: 'Research' },
  { value: 'intake', label: 'Intake' },
  { value: 'voice-dictation', label: 'Voice Dictation' },
];

const PRIORITY_OPTIONS: { value: NotePriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const CONFIDENTIALITY_OPTIONS: { value: NoteConfidentiality; label: string }[] = [
  { value: 'public', label: 'Public' },
  { value: 'attorney-work-product', label: 'Attorney Work Product' },
  { value: 'attorney-client-privilege', label: 'Attorney-Client Privilege' },
];

const SIGNIFICANCE_OPTIONS: { value: NoteLegalSignificance; label: string }[] = [
  { value: 'routine', label: 'Routine' },
  { value: 'important', label: 'Important' },
  { value: 'critical', label: 'Critical' },
  { value: 'case-dispositive', label: 'Case Dispositive' },
];

const PHASE_OPTIONS: { value: NoteCasePhase; label: string }[] = [
  { value: 'intake', label: 'Intake' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'motions', label: 'Motions' },
  { value: 'trial-prep', label: 'Trial Prep' },
  { value: 'trial', label: 'Trial' },
  { value: 'appeal', label: 'Appeal' },
  { value: 'closed', label: 'Closed' },
];

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

type RemoteCursor = {
  user_id: string;
  user_email?: string;
  cursor_position: number;
  selection_data?: any;
  timestamp: number;
};

function getCurrentSocketUserId(): string | null {
  const parsedSub = (window as any)?.keycloak?.tokenParsed?.sub;
  if (typeof parsedSub === 'string' && parsedSub.length > 0) {
    return parsedSub;
  }

  const token =
    (window as any)?.keycloak?.token ||
    localStorage.getItem('webelieve_access_token') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('webelieve_access_token') ||
    sessionStorage.getItem('token');

  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return typeof payload?.sub === 'string' ? payload.sub : null;
  } catch {
    return null;
  }
}

function getEditorContentEditable(root: HTMLElement | null): HTMLElement | null {
  if (!root) return null;
  return root.querySelector('.lexical-content-editable') as HTMLElement | null;
}

function getSelectionOffsetFromEditable(editable: HTMLElement): number | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  if (!editable.contains(range.endContainer)) return null;

  try {
    const prefixRange = document.createRange();
    prefixRange.selectNodeContents(editable);
    prefixRange.setEnd(range.endContainer, range.endOffset);
    return prefixRange.toString().length;
  } catch {
    return null;
  }
}

function getCursorCoordinates(editable: HTMLElement, offset: number): { left: number; top: number; height: number } | null {
  const safeOffset = Math.max(0, Math.floor(offset));
  const range = document.createRange();

  let targetNode: Text | null = null;
  let nodeOffset = 0;
  let traversed = 0;
  const walker = document.createTreeWalker(editable, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    const textNode = walker.currentNode as Text;
    const textLength = textNode.nodeValue?.length || 0;

    if (safeOffset <= traversed + textLength) {
      targetNode = textNode;
      nodeOffset = safeOffset - traversed;
      break;
    }

    traversed += textLength;
  }

  if (targetNode) {
    range.setStart(targetNode, Math.max(0, Math.min(nodeOffset, targetNode.length)));
    range.collapse(true);
  } else {
    range.selectNodeContents(editable);
    range.collapse(false);
  }

  const rect = range.getClientRects()[0] || range.getBoundingClientRect();
  const rootRect = editable.getBoundingClientRect();

  if (!rect || !Number.isFinite(rect.left) || !Number.isFinite(rect.top)) {
    return null;
  }

  const lineHeight = Number.parseFloat(window.getComputedStyle(editable).lineHeight) || 20;

  return {
    left: rect.left - rootRect.left + editable.scrollLeft,
    top: rect.top - rootRect.top + editable.scrollTop,
    height: rect.height || lineHeight,
  };
}

function hashToColor(value: string): string {
  const palette = ['#2563EB', '#16A34A', '#DC2626', '#EA580C', '#7C3AED', '#0891B2', '#BE123C'];
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(i);
    hash |= 0;
  }
  return palette[Math.abs(hash) % palette.length];
}

function getOptionLabel<T extends string>(
  options: Array<{ value: T; label: string }>,
  value: T | '' | null | undefined,
  fallback = 'None'
): string {
  if (!value) return fallback;
  return options.find((option) => option.value === value)?.label || fallback;
}

function hasValidLexicalRoot(editorState: unknown): editorState is Record<string, any> {
  if (!editorState || typeof editorState !== 'object') return false;
  const root = (editorState as any).root;
  return Boolean(root && typeof root === 'object' && root.type === 'root' && Array.isArray(root.children));
}

function buildLexicalStateFromText(text: string): Record<string, any> {
  const normalized = String(text || '').replace(/\r\n/g, '\n');
  const lines = normalized.length > 0 ? normalized.split('\n') : [''];

  return {
    root: {
      type: 'root',
      format: '',
      indent: 0,
      version: 1,
      direction: 'ltr',
      children: lines.map((line) => ({
        type: 'paragraph',
        format: '',
        indent: 0,
        version: 1,
        direction: 'ltr',
        children: [
          {
            type: 'text',
            detail: 0,
            format: 0,
            mode: 'normal',
            style: '',
            text: line,
            version: 1,
          },
        ],
      })),
    },
  };
}

const NoteEditor: React.FC<NoteEditorProps> = ({
  note,
  clientId,
  matterId,
  focusMode = false,
  onSetFocusMode,
}) => {
  const { updateNote, createNote, patchLocalNote } = useNotesStore();
  const {
    inviteUser,
    fetchCollaborators,
    fetchComments,
    fetchCollaborationState,
  } = useNoteCollaborationStore();

  // Context menu state & actions
  const { contextMenuState, openContextMenu, closeContextMenu } = useNoteContextMenu();
  const {
    handleAskLex,
    handleRewrite,
    handleCitationLookup,
    handleCreateTask,
    handleAddToTimeline,
    taskFeedback,
  } = useNoteContextActions({
    selectedText: contextMenuState.selectedText,
    noteTitle: note?.title || 'Untitled Note',
    noteId: note?.note_id || '',
    matterId,
    clientId,
    closeContextMenu,
  });

  // Local state for editing
  const [title, setTitle] = useState('');
  const [contentText, setContentText] = useState('');
  const [contentJson, setContentJson] = useState<Record<string, any>>({});
  const [noteType, setNoteType] = useState<NoteType>('general');
  const [priority, setPriority] = useState<NotePriority>('medium');
  const [confidentiality, setConfidentiality] = useState<NoteConfidentiality>('attorney-work-product');
  const [legalSignificance, setLegalSignificance] = useState<NoteLegalSignificance>('routine');
  const [casePhase, setCasePhase] = useState<NoteCasePhase | ''>('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [showMetadataPanel, setShowMetadataPanel] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [showPrivilegeWarning, setShowPrivilegeWarning] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);
  const [editorInstanceRevision, setEditorInstanceRevision] = useState(0);

  // Realtime/presence state
  const [realtimeConnected, setRealtimeConnected] = useState(noteCollaborationSocket.isConnected());
  const [collaborationError, setCollaborationError] = useState<string | null>(null);
  const [activeUsersById, setActiveUsersById] = useState<Record<string, {
    user_id: string;
    user_email?: string;
    timestamp: number;
    chat_ping_until?: number;
  }>>({});
  const [remoteCursorsByUserId, setRemoteCursorsByUserId] = useState<Record<string, RemoteCursor>>({});
  const [cursorLayoutRevision, setCursorLayoutRevision] = useState(0);

  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const realtimeBroadcastTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const noteIdRef = useRef<string | null>(null);
  const isNewRef = useRef(false);
  const isApplyingRemoteUpdateRef = useRef(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const editorSurfaceRef = useRef<HTMLDivElement>(null);
  const editorScrollRef = useRef<HTMLDivElement>(null);
  const cursorEmitRef = useRef<{ offset: number; at: number }>({ offset: -1, at: 0 });

  const socketUserId = useMemo(() => getCurrentSocketUserId(), []);

  const syncNoteListPreview = useCallback((patch: Partial<Note>, sourceNoteId?: string | null) => {
    const currentNoteId = noteIdRef.current;
    if (!currentNoteId || isNewRef.current) return;
    if (sourceNoteId !== undefined && (sourceNoteId || null) !== (currentNoteId || null)) return;

    patchLocalNote(currentNoteId, {
      ...patch,
      updated_at: new Date().toISOString(),
    });
  }, [patchLocalNote]);

  const applyNoteToLocalState = useCallback((sourceNote: Note) => {
    noteIdRef.current = sourceNote.note_id;
    isNewRef.current = false;
    const hydratedContentText = sourceNote.content_text || '';
    const hydratedContentJson = hasValidLexicalRoot(sourceNote.content_json)
      ? (sourceNote.content_json as Record<string, any>)
      : buildLexicalStateFromText(hydratedContentText);

    setTitle(sourceNote.title);
    setContentText(hydratedContentText);
    setContentJson(hydratedContentJson);
    setNoteType(sourceNote.note_type);
    setPriority(sourceNote.priority);
    setConfidentiality(sourceNote.confidentiality);
    setLegalSignificance((sourceNote.legal_significance as NoteLegalSignificance) || 'routine');
    setCasePhase((sourceNote.case_phase as NoteCasePhase) || '');
    setTags(sourceNote.tags || []);
    setSaveStatus('idle');
  }, []);

  const scheduleRealtimeBroadcast = useCallback((sourceNoteId?: string | null, overrides?: {
    title?: string;
    content_text?: string;
    content_json?: Record<string, any>;
  }) => {
    if (isNewRef.current || isApplyingRemoteUpdateRef.current) return;
    const currentNoteId = noteIdRef.current;
    if (!currentNoteId) return;
    if (sourceNoteId !== undefined && (sourceNoteId || null) !== (currentNoteId || null)) return;

    if (realtimeBroadcastTimerRef.current) {
      clearTimeout(realtimeBroadcastTimerRef.current);
    }

    const scheduledNoteId = currentNoteId;

    realtimeBroadcastTimerRef.current = setTimeout(() => {
      if ((noteIdRef.current || null) !== (scheduledNoteId || null)) return;

      noteCollaborationSocket.emitContentChanged(scheduledNoteId, {
        title: (overrides?.title ?? title).trim(),
        content_text: overrides?.content_text ?? contentText,
        content_json: overrides?.content_json ?? contentJson,
        version: Date.now(),
      });
    }, 220);
  }, [title, contentText, contentJson]);

  const emitCursorPosition = useCallback(() => {
    const currentNoteId = noteIdRef.current;
    if (!currentNoteId || isApplyingRemoteUpdateRef.current || isNewRef.current) return;

    const editable = getEditorContentEditable(editorSurfaceRef.current);
    if (!editable) return;

    const offset = getSelectionOffsetFromEditable(editable);
    if (offset === null) return;

    const now = Date.now();
    if (offset === cursorEmitRef.current.offset && now - cursorEmitRef.current.at < 900) {
      return;
    }

    cursorEmitRef.current = { offset, at: now };
    noteCollaborationSocket.emitCursorMoved(currentNoteId, offset);
  }, []);

  // Sync local state only when switching to a different note ID.
  // Do not resync on same-note object updates, which happen during local preview patching.
  useEffect(() => {
    if (note) {
      applyNoteToLocalState(note);
    } else {
      // New note mode
      noteIdRef.current = null;
      isNewRef.current = true;
      setTitle('');
      setContentText('');
      setContentJson({});
      setNoteType('general');
      setPriority('medium');
      setConfidentiality('attorney-work-product');
      setLegalSignificance('routine');
      setCasePhase('');
      setTags([]);
      setSaveStatus('idle');
    }

    setActiveUsersById({});
    setRemoteCursorsByUserId({});
    setCollaborationError(null);
    setShowMetadataPanel(false);
  }, [note?.note_id, applyNoteToLocalState]);

  useEffect(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    if (realtimeBroadcastTimerRef.current) clearTimeout(realtimeBroadcastTimerRef.current);
  }, [note?.note_id]);

  useEffect(() => {
    setEditorInstanceRevision(0);
  }, [note?.note_id]);

  useEffect(() => {
    if (!note && isNewRef.current) {
      titleInputRef.current?.focus();
    }
  }, [note?.note_id]);

  // Auto-save on content/metadata changes
  const triggerAutoSave = useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

    const scheduledNoteId = noteIdRef.current;
    const scheduledIsNew = isNewRef.current;

    autoSaveTimerRef.current = setTimeout(async () => {
      if ((noteIdRef.current || null) !== (scheduledNoteId || null)) return;
      if (isNewRef.current !== scheduledIsNew) return;

      if (scheduledIsNew) {
        if (!title.trim()) return;
        setSaveStatus('saving');
        const created = await createNote({
          title: title.trim(),
          content_text: contentText,
          content_json: contentJson,
          note_type: noteType,
          priority,
          confidentiality,
          legal_significance: legalSignificance,
          case_phase: casePhase || undefined,
          tags,
          client_id: clientId,
          matter_id: matterId,
        });
        if (created) {
          noteIdRef.current = created.note_id;
          isNewRef.current = false;
          setSaveStatus('saved');
        } else {
          setSaveStatus('error');
        }
      } else if (scheduledNoteId) {
        setSaveStatus('saving');
        const dto: UpdateNoteDto = {
          title: title.trim(),
          content_text: contentText,
          content_json: contentJson,
          note_type: noteType,
          priority,
          confidentiality,
          legal_significance: legalSignificance,
          case_phase: casePhase || undefined,
          tags,
        };
        const result = await updateNote(scheduledNoteId, dto);
        setSaveStatus(result ? 'saved' : 'error');
      }
    }, AUTO_SAVE_DELAY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, contentText, contentJson, noteType, priority, confidentiality, legalSignificance, casePhase, tags, clientId, matterId]);

  // Watch for changes to trigger auto-save
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (isApplyingRemoteUpdateRef.current) {
      isApplyingRemoteUpdateRef.current = false;
      return;
    }
    triggerAutoSave();
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [triggerAutoSave]);

  // Reset first render flag when note changes
  useEffect(() => {
    isFirstRender.current = true;
  }, [note?.note_id]);

  // Attach DOM-based cursor tracking for local presence
  useEffect(() => {
    if (!note?.note_id) return;

    let cancelled = false;
    let rafId = 0;
    let cleanup: (() => void) | null = null;

    const bindListeners = () => {
      const editable = getEditorContentEditable(editorSurfaceRef.current);
      if (!editable) {
        rafId = requestAnimationFrame(bindListeners);
        return;
      }

      if (cancelled) return;

      const handler = () => emitCursorPosition();
      editable.addEventListener('keyup', handler);
      editable.addEventListener('mouseup', handler);
      editable.addEventListener('input', handler);
      editable.addEventListener('focus', handler);
      document.addEventListener('selectionchange', handler);

      cleanup = () => {
        editable.removeEventListener('keyup', handler);
        editable.removeEventListener('mouseup', handler);
        editable.removeEventListener('input', handler);
        editable.removeEventListener('focus', handler);
        document.removeEventListener('selectionchange', handler);
      };

      emitCursorPosition();
    };

    bindListeners();

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      cleanup?.();
    };
  }, [note?.note_id, editorInstanceRevision, emitCursorPosition]);

  // Realtime collaboration wiring
  useEffect(() => {
    const currentNoteId = note?.note_id;
    if (!currentNoteId) return;

    const unsubscribers: Array<() => void> = [];
    let disposed = false;

    const setupRealtime = async () => {
      const connected = await noteCollaborationSocket.connect();
      if (disposed) return;

      setRealtimeConnected(connected);

      if (!connected) {
        setCollaborationError('Realtime collaboration unavailable. Changes continue via autosave.');
        return;
      }

      setCollaborationError(null);
      await fetchCollaborationState(currentNoteId);
      await noteCollaborationSocket.joinNote(currentNoteId);

      unsubscribers.push(
        noteCollaborationSocket.on('connect', () => setRealtimeConnected(true))
      );

      unsubscribers.push(
        noteCollaborationSocket.on('disconnect', () => setRealtimeConnected(false))
      );

      unsubscribers.push(
        noteCollaborationSocket.on('collaboration-error', (payload: any) => {
          if (disposed) return;
          setCollaborationError(payload?.error || 'Collaboration error');
        })
      );

      unsubscribers.push(
        noteCollaborationSocket.on('note-collaboration-state', (payload: any) => {
          if (disposed || payload?.note_id !== currentNoteId) return;
          const activeUsers = Array.isArray(payload?.active_users) ? payload.active_users : [];

          setActiveUsersById((current) => {
            const next: Record<string, {
              user_id: string;
              user_email?: string;
              timestamp: number;
              chat_ping_until?: number;
            }> = {};
            activeUsers.forEach((entry: any) => {
              if (!entry?.user_id || entry.user_id === socketUserId) return;
              next[entry.user_id] = {
                user_id: entry.user_id,
                user_email: entry.user_email,
                timestamp: Date.now(),
                chat_ping_until: current[entry.user_id]?.chat_ping_until,
              };
            });
            return next;
          });

          setRemoteCursorsByUserId(() => {
            const next: Record<string, RemoteCursor> = {};
            activeUsers.forEach((entry: any) => {
              if (!entry?.user_id || entry.user_id === socketUserId) return;
              if (typeof entry.cursor_position !== 'number') return;

              next[entry.user_id] = {
                user_id: entry.user_id,
                user_email: entry.user_email,
                cursor_position: entry.cursor_position,
                selection_data: entry.selection_data,
                timestamp: Date.now(),
              };
            });
            return next;
          });

          setCursorLayoutRevision((value) => value + 1);
        })
      );

      unsubscribers.push(
        noteCollaborationSocket.on('note-content-changed', (payload: any) => {
          if (disposed || payload?.note_id !== currentNoteId) return;
          if (socketUserId && payload?.user_id === socketUserId) return;

          const hasContentPatch =
            typeof payload?.title === 'string' ||
            typeof payload?.content_text === 'string' ||
            (payload?.content_json && typeof payload.content_json === 'object');
          if (!hasContentPatch) return;

          isApplyingRemoteUpdateRef.current = true;
          if (typeof payload.title === 'string') setTitle(payload.title);
          if (typeof payload.content_text === 'string') setContentText(payload.content_text);
          if (payload.content_json && typeof payload.content_json === 'object') {
            setContentJson(payload.content_json);
            setEditorInstanceRevision((prev) => prev + 1);
          }
          syncNoteListPreview({
            ...(typeof payload.title === 'string' ? { title: payload.title } : {}),
            ...(typeof payload.content_text === 'string' ? { content_text: payload.content_text } : {}),
            ...(payload.content_json && typeof payload.content_json === 'object' ? { content_json: payload.content_json } : {}),
          });
          setSaveStatus('saved');
        })
      );

      unsubscribers.push(
        noteCollaborationSocket.on('note-user-joined', (payload: any) => {
          if (disposed || payload?.note_id !== currentNoteId || !payload?.user_id) return;
          if (socketUserId && payload.user_id === socketUserId) return;

          setActiveUsersById((current) => ({
            ...current,
            [payload.user_id]: {
              user_id: payload.user_id,
              user_email: payload.user_email || current[payload.user_id]?.user_email,
              timestamp: Date.now(),
              chat_ping_until: current[payload.user_id]?.chat_ping_until,
            },
          }));

          fetchCollaborators(currentNoteId);
        })
      );

      unsubscribers.push(
        noteCollaborationSocket.on('note-user-left', (payload: any) => {
          if (disposed || payload?.note_id !== currentNoteId || !payload?.user_id) return;

          setActiveUsersById((current) => {
            const next = { ...current };
            delete next[payload.user_id];
            return next;
          });

          setRemoteCursorsByUserId((current) => {
            const next = { ...current };
            delete next[payload.user_id];
            return next;
          });

          fetchCollaborators(currentNoteId);
        })
      );

      unsubscribers.push(
        noteCollaborationSocket.on('note-cursor-moved', (payload: any) => {
          if (disposed || payload?.note_id !== currentNoteId || !payload?.user_id) return;
          if (socketUserId && payload.user_id === socketUserId) return;
          if (typeof payload.cursor_position !== 'number') return;

          setActiveUsersById((current) => ({
            ...current,
            [payload.user_id]: {
              user_id: payload.user_id,
              user_email: payload.user_email || current[payload.user_id]?.user_email,
              timestamp: Date.now(),
              chat_ping_until: current[payload.user_id]?.chat_ping_until,
            },
          }));

          setRemoteCursorsByUserId((current) => ({
            ...current,
            [payload.user_id]: {
              user_id: payload.user_id,
              user_email: payload.user_email,
              cursor_position: payload.cursor_position,
              selection_data: payload.selection_data,
              timestamp: Date.now(),
            },
          }));

          setCursorLayoutRevision((value) => value + 1);
        })
      );

      unsubscribers.push(
        noteCollaborationSocket.on('note-comment-added', (payload: any) => {
          if (disposed || payload?.note_id !== currentNoteId) return;
          if (payload?.user_id) {
            setActiveUsersById((current) => ({
              ...current,
              [payload.user_id]: {
                user_id: payload.user_id,
                user_email: payload.user_email || current[payload.user_id]?.user_email,
                timestamp: Date.now(),
                chat_ping_until: Date.now() + COMMENT_PING_MS,
              },
            }));
          }
          fetchComments(currentNoteId);
        })
      );

      unsubscribers.push(
        noteCollaborationSocket.on('note-comment-resolved', (payload: any) => {
          if (disposed || payload?.note_id !== currentNoteId) return;
          fetchComments(currentNoteId);
        })
      );
    };

    setupRealtime();

    return () => {
      disposed = true;
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      noteCollaborationSocket.leaveNote(currentNoteId);
    };
  }, [note?.note_id, socketUserId, fetchCollaborators, fetchComments, fetchCollaborationState, syncNoteListPreview]);

  // Remove stale remote presence records
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();

      setRemoteCursorsByUserId((current) => {
        const next: Record<string, RemoteCursor> = {};
        Object.entries(current).forEach(([key, value]) => {
          if (now - value.timestamp < CURSOR_STALE_MS) {
            next[key] = value;
          }
        });
        return next;
      });

      setActiveUsersById((current) => {
        const next: Record<string, {
          user_id: string;
          user_email?: string;
          timestamp: number;
          chat_ping_until?: number;
        }> = {};
        Object.entries(current).forEach(([key, value]) => {
          if (now - value.timestamp < PRESENCE_STALE_MS) {
            if (typeof value.chat_ping_until === 'number' && value.chat_ping_until <= now) {
              next[key] = {
                ...value,
                chat_ping_until: undefined,
              };
            } else {
              next[key] = value;
            }
          }
        });
        return next;
      });
    }, 5000);

    return () => clearInterval(cleanupInterval);
  }, []);

  // Recalculate cursor overlays when viewport/scroll changes
  useEffect(() => {
    const scrollEl = editorScrollRef.current;
    const handleReflow = () => setCursorLayoutRevision((value) => value + 1);

    scrollEl?.addEventListener('scroll', handleReflow, { passive: true });
    window.addEventListener('resize', handleReflow);

    return () => {
      scrollEl?.removeEventListener('scroll', handleReflow);
      window.removeEventListener('resize', handleReflow);
    };
  }, [note?.note_id, editorInstanceRevision]);

  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (realtimeBroadcastTimerRef.current) clearTimeout(realtimeBroadcastTimerRef.current);
    };
  }, []);

  const handleEditorChange = (text: string) => {
    const sourceNoteId = note?.note_id || null;
    if ((noteIdRef.current || null) !== sourceNoteId) return;

    setContentText(text);
    syncNoteListPreview({ content_text: text }, sourceNoteId);
    scheduleRealtimeBroadcast(sourceNoteId, { content_text: text });
    requestAnimationFrame(() => emitCursorPosition());
  };

  const handleEditorJsonChange = (json: string) => {
    const sourceNoteId = note?.note_id || null;
    if ((noteIdRef.current || null) !== sourceNoteId) return;

    try {
      const parsedJson = JSON.parse(json);
      setContentJson(parsedJson);
      syncNoteListPreview({ content_json: parsedJson }, sourceNoteId);
      scheduleRealtimeBroadcast(sourceNoteId, { content_json: parsedJson });
    } catch {
      // ignore parse errors during editing
    }
  };

  const handleTitleChange = (nextTitle: string) => {
    const sourceNoteId = note?.note_id || null;
    if ((noteIdRef.current || null) !== sourceNoteId) return;

    setTitle(nextTitle);
    syncNoteListPreview({ title: nextTitle }, sourceNoteId);
    scheduleRealtimeBroadcast(sourceNoteId, { title: nextTitle });
  };

  const handleNoteTypeChange = (nextValue: NoteType) => {
    setNoteType(nextValue);
    syncNoteListPreview({ note_type: nextValue });
  };

  const handlePriorityChange = (nextValue: NotePriority) => {
    setPriority(nextValue);
    syncNoteListPreview({ priority: nextValue });
  };

  const handleConfidentialityChange = (nextValue: NoteConfidentiality) => {
    setConfidentiality(nextValue);
    syncNoteListPreview({ confidentiality: nextValue });
  };

  const handleLegalSignificanceChange = (nextValue: NoteLegalSignificance) => {
    setLegalSignificance(nextValue);
    syncNoteListPreview({ legal_significance: nextValue });
  };

  const handleCasePhaseChange = (nextValue: NoteCasePhase | '') => {
    setCasePhase(nextValue);
    syncNoteListPreview({ case_phase: nextValue || null });
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      const nextTags = [...tags, trimmed];
      setTags(nextTags);
      syncNoteListPreview({ tags: nextTags });
    }
    setTagInput('');
  };

  const handleRemoveTag = (tag: string) => {
    const nextTags = tags.filter((t) => t !== tag);
    setTags(nextTags);
    syncNoteListPreview({ tags: nextTags });
  };

  const handleShareClick = () => {
    if (!note) return;
    if (confidentiality !== 'public') {
      setShowPrivilegeWarning(true);
    } else {
      setShowInviteModal(true);
    }
  };

  const handlePrivilegeConfirm = () => {
    setShowPrivilegeWarning(false);
    setShowInviteModal(true);
  };

  const handleInviteSubmit = async (payload: {
    user_id: string;
    role: 'editor' | 'reviewer' | 'viewer';
    message?: string;
    expires_in_hours?: number;
  }) => {
    if (!note) return;

    await inviteUser(note.note_id, payload);
    fetchCollaborators(note.note_id);
    fetchCollaborationState(note.note_id);
  };

  const activePresenceCount = useMemo(() => Object.keys(activeUsersById).length, [activeUsersById]);
  const wordCount = useMemo(() => {
    const trimmed = contentText.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).length;
  }, [contentText]);
  const characterCount = contentText.length;
  const estimatedReadMinutes = wordCount === 0 ? 0 : Math.max(1, Math.ceil(wordCount / 220));

  const metadataSummaryChips = useMemo(() => ([
    { label: 'Type', value: getOptionLabel(NOTE_TYPE_OPTIONS, noteType, 'General') },
    { label: 'Priority', value: getOptionLabel(PRIORITY_OPTIONS, priority, 'Medium') },
    { label: 'Confidentiality', value: getOptionLabel(CONFIDENTIALITY_OPTIONS, confidentiality, 'Public') },
    { label: 'Significance', value: getOptionLabel(SIGNIFICANCE_OPTIONS, legalSignificance, 'Routine') },
    { label: 'Phase', value: getOptionLabel(PHASE_OPTIONS, casePhase, 'No Phase') },
  ]), [noteType, priority, confidentiality, legalSignificance, casePhase]);

  const effectiveEditorJson = useMemo(() => {
    if (note && note.note_id !== noteIdRef.current) {
      return (note.content_json as Record<string, any>) || {};
    }
    return contentJson;
  }, [note, contentJson]);

  const remoteCursorOverlays = useMemo(() => {
    const editable = getEditorContentEditable(editorSurfaceRef.current);
    if (!editable) return [] as Array<{ key: string; email: string; color: string; left: number; top: number; height: number }>;

    const overlays: Array<{ key: string; email: string; color: string; left: number; top: number; height: number }> = [];
    Object.entries(remoteCursorsByUserId).forEach(([userId, cursor]) => {
      const coordinates = getCursorCoordinates(editable, cursor.cursor_position);
      if (!coordinates) return;

      overlays.push({
        key: userId,
        email: cursor.user_email || 'Collaborator',
        color: hashToColor(userId),
        left: coordinates.left,
        top: coordinates.top,
        height: coordinates.height,
      });
    });

    return overlays;
  }, [remoteCursorsByUserId, cursorLayoutRevision, editorInstanceRevision]);

  // Empty state
  if (!note && !isNewRef.current) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-8 bg-gray-50 dark:bg-gray-900">
        <svg className="w-16 h-16 text-gray-200 dark:text-gray-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-400 dark:text-gray-500 mb-1">
          Select a note to edit
        </h3>
        <p className="text-sm text-gray-300 dark:text-gray-600">
          Choose a note from the list or create a new one
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex h-full min-h-0 bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Main editor area */}
      <div className={`flex-1 min-w-0 ${focusMode ? 'overflow-hidden' : 'overflow-y-auto'}`}>
        <div className={focusMode ? 'flex h-full w-full flex-col' : 'mx-auto flex w-full max-w-[1680px] flex-col gap-3 p-4 md:p-5'}>
          {/* Header + lightweight metadata summary */}
          {!focusMode && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <input
                  ref={titleInputRef}
                  type="text"
                  value={title}
                  onChange={(event) => handleTitleChange(event.target.value)}
                  placeholder="Note title..."
                  className="flex-1 text-2xl font-semibold bg-transparent text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none border-none"
                />
                <div className="flex items-center">
                  <span className={`text-xs font-medium shrink-0 ${
                    saveStatus === 'saving' ? 'text-lex-accent' :
                    saveStatus === 'saved' ? 'text-green-500' :
                    saveStatus === 'error' ? 'text-red-500' :
                    'text-gray-400'
                  }`}>
                    {saveStatus === 'saving' && 'Saving...'}
                    {saveStatus === 'saved' && 'Saved'}
                    {saveStatus === 'error' && 'Save failed'}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  {metadataSummaryChips.map((chip) => (
                    <span
                      key={chip.label}
                      className="inline-flex items-center gap-1 rounded-full border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:text-gray-200"
                    >
                      <span className="text-gray-400 dark:text-gray-400">{chip.label}:</span>
                      <span>{chip.value}</span>
                    </span>
                  ))}
                  <button
                    type="button"
                    onClick={() => setShowMetadataPanel((value) => !value)}
                    className="inline-flex items-center rounded-full border border-lex-accent bg-lex-accent-soft px-2.5 py-1 text-[11px] font-semibold text-lex-accent"
                  >
                    {showMetadataPanel ? 'Hide Details' : 'Edit Details'}
                  </button>
                </div>
                <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                  {wordCount} words • {characterCount} chars • {estimatedReadMinutes} min read
                </div>
              </div>

              {note && (
                <div className="flex items-center justify-end">
                  <NoteCollaborationToolbar
                    noteId={note.note_id}
                    confidentiality={confidentiality}
                    onCommentsToggle={() => setCommentsOpen(!commentsOpen)}
                    commentsOpen={commentsOpen}
                    onShareClick={handleShareClick}
                    onSettingsClick={() => setShowSettingsDrawer(true)}
                    activePresenceCount={activePresenceCount}
                    realtimeConnected={realtimeConnected}
                    activeUsers={Object.values(activeUsersById)}
                  />
                </div>
              )}

              {collaborationError && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400">
                  {collaborationError}
                </p>
              )}
            </div>
            </div>
          )}

          {/* Progressive metadata controls */}
          {!focusMode && showMetadataPanel && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300 mb-3">
                Note Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Type</label>
                  <select
                    value={noteType}
                    onChange={(event) => handleNoteTypeChange(event.target.value as NoteType)}
                    className="w-full text-sm px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                  >
                    {NOTE_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Priority</label>
                  <select
                    value={priority}
                    onChange={(event) => handlePriorityChange(event.target.value as NotePriority)}
                    className="w-full text-sm px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                  >
                    {PRIORITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Confidentiality</label>
                  <select
                    value={confidentiality}
                    onChange={(event) => handleConfidentialityChange(event.target.value as NoteConfidentiality)}
                    className="w-full text-sm px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                  >
                    {CONFIDENTIALITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Significance</label>
                  <select
                    value={legalSignificance}
                    onChange={(event) => handleLegalSignificanceChange(event.target.value as NoteLegalSignificance)}
                    className="w-full text-sm px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                  >
                    {SIGNIFICANCE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Case Phase</label>
                  <select
                    value={casePhase}
                    onChange={(event) => handleCasePhaseChange(event.target.value as NoteCasePhase | '')}
                    className="w-full text-sm px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                  >
                    <option value="">No Phase</option>
                    {PHASE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Tags</label>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-lex-accent-soft text-lex-accent rounded-full"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="text-lex-accent hover:text-lex-accent-hover"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(event) => setTagInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleAddTag();
                      }
                    }}
                    placeholder="Add tag..."
                    className="w-full max-w-xs text-sm px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-3 py-2 text-xs font-medium rounded-lg bg-lex-accent text-lex-accent-contrast hover:bg-lex-accent-hover"
                  >
                    Add Tag
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Rich editor surface */}
          <div className={`${focusMode ? 'flex flex-col flex-1 min-h-0 rounded-none border-0 shadow-none' : 'rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm'} bg-white dark:bg-gray-800 overflow-hidden`}>
            <div className={`px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between${focusMode ? ' flex-shrink-0' : ''}`}>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-800 dark:text-gray-200">
                {focusMode ? 'Focus Mode' : 'Note Content'}
              </h3>
              <div className="flex items-center gap-3">
                {!focusMode && (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    Rich editor with headings, lists, links, and legal mentions (@ # $ /doc)
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => onSetFocusMode?.(!focusMode)}
                  className="inline-flex items-center rounded-md border border-gray-200 dark:border-gray-600 px-2.5 py-1 text-[11px] font-semibold text-gray-600 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {focusMode ? 'Exit Focus' : 'Focus Mode'}
                </button>
              </div>
            </div>
            <div
              ref={editorScrollRef}
              className={focusMode ? 'note-focus-scroll-host flex-1 min-h-0 overflow-y-scroll overflow-x-hidden p-3 md:p-4' : 'min-h-[420px] overflow-y-auto overflow-x-hidden p-4 md:p-5'}
            >
              <div
                ref={editorSurfaceRef}
                onFocusCapture={() => onSetFocusMode?.(true)}
                className={focusMode ? 'relative min-h-full min-w-0' : 'relative min-h-[380px] min-w-0 overflow-hidden'}
              >
                <LexicalEventEditor
                  placeholder="Start writing your note... Use @ for contacts, # for matters, $ for bills"
                  initialJson={effectiveEditorJson}
                  editorKey={`${note?.note_id || 'new-note'}:${editorInstanceRevision}`}
                  onChange={handleEditorChange}
                  onJsonChange={handleEditorJsonChange}
                  onContextMenu={openContextMenu}
                  showToolbar
                  fullHeight={focusMode}
                  className={focusMode ? 'min-h-full' : ''}
                  contentEditableClassName={focusMode ? 'min-h-full text-[15px] leading-7' : 'min-h-[380px] text-[15px] leading-7'}
                />

                {remoteCursorOverlays.map((cursor) => (
                  <div
                    key={cursor.key}
                    className="absolute pointer-events-none z-20"
                    style={{ left: cursor.left, top: cursor.top }}
                  >
                    <div style={{ borderLeft: `2px solid ${cursor.color}`, height: Math.max(cursor.height, 16) }} />
                    <div
                      className="absolute -top-5 left-0 px-1.5 py-0.5 rounded text-[10px] text-white whitespace-nowrap"
                      style={{ backgroundColor: cursor.color }}
                    >
                      {cursor.email}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {!focusMode && note?.intelligence && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm p-4">
              <NoteIQBadge intelligence={note.intelligence} />
            </div>
          )}

          {!focusMode && note && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm px-4 py-2 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
              <span>Created by {note.created_by_name || 'Unknown'} on {new Date(note.created_at).toLocaleDateString()}</span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowVersionHistory(true)}
                  className="hover:text-lex-accent transition-colors"
                >
                  Version History
                </button>
                <span>Last updated {new Date(note.updated_at).toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Comment panel — slides in from right */}
      {note && (
        <NoteCommentPanel
          isOpen={commentsOpen}
          onClose={() => setCommentsOpen(false)}
          noteId={note.note_id}
        />
      )}

      {/* Privilege warning modal */}
      {note && (
        <NotePrivilegeWarning
          isOpen={showPrivilegeWarning}
          confidentiality={confidentiality}
          noteTitle={title || note.title}
          onConfirm={handlePrivilegeConfirm}
          onCancel={() => setShowPrivilegeWarning(false)}
        />
      )}

      {/* Version history drawer */}
      {note && (
        <NoteVersionHistory
          isOpen={showVersionHistory}
          onClose={() => setShowVersionHistory(false)}
          noteId={note.note_id}
          onRestore={(restoredNote) => {
            applyNoteToLocalState(restoredNote);
            setEditorInstanceRevision((prev) => prev + 1);
            setShowVersionHistory(false);
          }}
        />
      )}

      {/* Invite modal */}
      {note && (
        <NoteInviteCollaboratorModal
          isOpen={showInviteModal}
          noteId={note.note_id}
          noteTitle={title || note.title}
          onClose={() => setShowInviteModal(false)}
          onInvite={handleInviteSubmit}
        />
      )}

      {/* Collaboration settings drawer */}
      {note && (
        <NoteCollaborationSettingsDrawer
          isOpen={showSettingsDrawer}
          noteId={note.note_id}
          noteTitle={title || note.title}
          onClose={() => setShowSettingsDrawer(false)}
          onInviteClick={() => {
            setShowSettingsDrawer(false);
            setShowInviteModal(true);
          }}
        />
      )}

      {/* Right-click context menu */}
      {contextMenuState.isOpen && (
        <NoteContextMenu
          x={contextMenuState.x}
          y={contextMenuState.y}
          onClose={closeContextMenu}
          onAskLex={handleAskLex}
          onRewrite={handleRewrite}
          onCitationLookup={handleCitationLookup}
          onCreateTask={handleCreateTask}
          onTagPrivileged={(level) => {
            closeContextMenu();
            handleConfidentialityChange(level as NoteConfidentiality);
          }}
          onAddToTimeline={handleAddToTimeline}
        />
      )}

      {/* Task creation feedback toast */}
      {taskFeedback && (
        <div className={`fixed bottom-6 right-6 z-[9999] px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium
          ${taskFeedback.type === 'success'
            ? 'bg-green-600 text-white'
            : 'bg-red-600 text-white'
          }`}
        >
          {taskFeedback.message}
        </div>
      )}
    </div>
  );
};

export default NoteEditor;
